/**
 * passkey-budget WP3: the approval tray. Every risky command waits here; the sheet lists exactly
 * what will be signed (one row per action, production releases called out) and one passkey
 * ceremony approves the chosen rows. "Later" keeps them waiting behind a small pill; "Cancel"
 * sends nothing. WebAuthn cannot show what is signed, so this sheet is where the user sees it.
 */
import { Button, Sheet } from '@viko/transcript-ui'
import { Fingerprint, Laptop, Rocket, Timer, X } from 'lucide-react'
import { useEffect, useState, useSyncExternalStore } from 'react'
import { useBackend } from '../lib/backend.js'
import { passkeyErrorMessage } from '../lib/passkeys.js'
import { useMachines } from '../lib/queries.js'

const KIND_LABEL: Record<string, string> = {
  'land.merge': 'Landing',
  'land.deploy': 'Build / release',
  'secret.approve': 'Secret',
  'decision.answer.risky': 'Risky answer',
  'work.delete-unmerged': 'Deletion',
  'project.policy': 'Machine policy',
  'preview.share_port': 'Share a port',
}

export function ApprovalTray() {
  const b = useBackend()
  const tray = b.tray
  const s = useSyncExternalStore(tray.subscribe, tray.snapshot, tray.snapshot)
  const machines = useMachines()
  const [unchecked, setUnchecked] = useState<ReadonlySet<string>>(new Set())
  const [error, setError] = useState<string>()
  // Rows that left the tray can't stay unchecked.
  useEffect(() => {
    setUnchecked((u) => new Set([...u].filter((id) => s.items.some((i) => i.id === id))))
  }, [s.items])
  if (!s.items.length) return null
  const name = (id: string) =>
    id
      .split(', ')
      .map((m) => machines.data?.find((x) => x.id === m)?.name ?? m)
      .join(', ')
  if (!s.open)
    return (
      <button
        type="button"
        className="fixed right-4 bottom-4 z-[900] flex items-center gap-2 rounded-full bg-accent px-4 py-2 text-[14px] font-medium text-on-accent shadow-lg"
        onClick={() => tray.show()}
      >
        <Fingerprint size={15} aria-hidden="true" />
        {s.items.length} waiting for your passkey
      </button>
    )
  const chosen = s.items.filter((i) => !unchecked.has(i.id))
  const single = s.items.length === 1 ? s.items[0] : undefined
  const approve = async () => {
    setError(undefined)
    try {
      await tray.approve(chosen.map((i) => i.id))
    } catch (e) {
      setError(passkeyErrorMessage(e))
    }
  }
  return (
    <Sheet
      open
      onClose={() => tray.later()}
      title="Confirm with your passkey"
      placement="center"
      actions={
        <>
          <Button variant="ghost" onClick={() => tray.decline(s.items.map((i) => i.id))}>
            Cancel
          </Button>
          {single ? null : (
            <Button variant="ghost" onClick={() => tray.later()}>
              Later
            </Button>
          )}
          <Button
            variant="primary"
            icon={<Fingerprint size={16} />}
            onClick={() => void approve()}
            disabled={s.busy || chosen.length === 0}
            autoFocus
          >
            {single ? 'Continue' : `Approve ${chosen.length} with one passkey`}
          </Button>
        </>
      }
    >
      <div className="grid gap-3 pb-1">
        <p className="m-0 text-[17px] font-semibold leading-6">
          {single ? single.summary : `${s.items.length} actions need your passkey`}
        </p>
        {s.production ? (
          <p className="m-0 flex items-center gap-2 rounded-lg bg-status-wait/15 px-3 py-2 text-[14px] font-medium">
            <Rocket size={15} aria-hidden="true" />
            Includes {s.production} production release{s.production === 1 ? '' : 's'}
          </p>
        ) : null}
        <ul className="m-0 grid list-none gap-2 p-0" aria-label="Actions to approve">
          {s.items.map((i) => (
            <li key={i.id} className="flex items-start gap-2 rounded-lg bg-surface-2 px-3 py-2">
              {single ? null : (
                <input
                  type="checkbox"
                  className="mt-1"
                  aria-label={`Include: ${i.summary}`}
                  checked={!unchecked.has(i.id)}
                  onChange={(e) =>
                    setUnchecked((u) => {
                      const n = new Set(u)
                      if (e.target.checked) n.delete(i.id)
                      else n.add(i.id)
                      return n
                    })
                  }
                />
              )}
              <span className="grid min-w-0 flex-1 gap-0.5">
                {single ? null : <span className="font-medium">{i.summary}</span>}
                <span className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[14px] text-text-2">
                  <span>{KIND_LABEL[i.kind] ?? i.kind}</span>
                  {i.production ? (
                    <span className="font-semibold text-status-wait">Production release</span>
                  ) : null}
                  <span className="flex items-center gap-1">
                    <Laptop size={14} aria-hidden="true" /> Runs on {name(i.machineId)}
                  </span>
                </span>
              </span>
              {single ? null : (
                <Button
                  variant="ghost"
                  size="sm"
                  icon={<X size={14} />}
                  aria-label={`Remove: ${i.summary}`}
                  onClick={() => tray.decline([i.id])}
                />
              )}
            </li>
          ))}
        </ul>
        <span className="flex items-center gap-2 text-[14px] text-text-2">
          <Timer size={15} aria-hidden="true" /> Each action is signed when you approve and expires
          in 5 min
        </span>
        <p className="m-0 text-[14px] text-text-3">
          Your passkey signs exactly {single ? 'this request' : 'the checked requests'}. Each
          machine checks its own signature before acting.
        </p>
        {error || s.error ? (
          <p role="alert" className="m-0 text-[14px] text-status-fail">
            {error ?? s.error}
          </p>
        ) : null}
      </div>
    </Sheet>
  )
}
